import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { User } from 'lucide-react';
import { COMPANY } from '../../config/company';

// Lead Status Options
const getLeadStatusOptions = (language) => [
  { value: 'estimate_provided', label: language === 'es' ? 'Presupuesto Enviado' : 'Estimate Provided', color: 'bg-blue-100 text-blue-700' },
  { value: 'follow_up', label: language === 'es' ? 'Seguimiento' : 'Follow Up', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'presentation_made', label: language === 'es' ? 'Presentación Hecha' : 'Presentation Made', color: 'bg-purple-100 text-purple-700' },
  { value: 'lead_won', label: language === 'es' ? 'Lead Ganado' : 'Lead Won', color: 'bg-green-100 text-green-700' },
  { value: 'lead_lost', label: language === 'es' ? 'Lead Perdido' : 'Lead Lost', color: 'bg-red-100 text-red-700' },
  { value: 'cold_lead', label: language === 'es' ? 'Lead Frío' : 'Cold Lead', color: 'bg-gray-100 text-gray-700' },
  { value: 'do_not_contact', label: language === 'es' ? 'No Contactar' : 'Do Not Contact', color: 'bg-red-200 text-red-800' }
];

// Project Categories
const ProjectCategorySelect = ({ language, value, onValueChange }) => (
  <Select value={value || ''} onValueChange={onValueChange}>
    <SelectTrigger><SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} /></SelectTrigger>
    <SelectContent>
      {/* Residential Projects */}
      <SelectItem value="bathroom">{language === 'es' ? 'Remodelación de Baño' : 'Bathroom Remodeling'}</SelectItem>
      <SelectItem value="concrete_work">{language === 'es' ? 'Trabajo de Concreto' : 'Concrete (Driveway/Patio)'}</SelectItem>
      <SelectItem value="drain_line_replacement">{language === 'es' ? 'Reemplazo de Drenaje' : 'Drain Line Replacement'}</SelectItem>
      <SelectItem value="drywall_repair">{language === 'es' ? 'Reparación de Drywall' : 'Drywall Repair'}</SelectItem>
      <SelectItem value="electrical_panel_upgrade">{language === 'es' ? 'Actualización de Panel Eléctrico' : 'Electrical Panel Upgrade'}</SelectItem>
      <SelectItem value="fence_installation">{language === 'es' ? 'Instalación de Cerca' : 'Fence Installation'}</SelectItem>
      <SelectItem value="flooring">{language === 'es' ? 'Pisos (Laminado)' : 'Flooring (Laminate)'}</SelectItem>
      <SelectItem value="gutter_installation">{language === 'es' ? 'Instalación de Canaletas' : 'Gutter Installation'}</SelectItem>
      <SelectItem value="home_addition">{language === 'es' ? 'Ampliación del Hogar' : 'Home Addition'}</SelectItem>
      <SelectItem value="home_renovation">{language === 'es' ? 'Renovación del Hogar' : 'Home Renovation'}</SelectItem>
      <SelectItem value="hvac_replacement">{language === 'es' ? 'Reemplazo de HVAC' : 'HVAC Replacement'}</SelectItem>
      <SelectItem value="interior_painting">{language === 'es' ? 'Pintura Interior' : 'Interior Painting'}</SelectItem>
      <SelectItem value="kitchen">{language === 'es' ? 'Remodelación de Cocina' : 'Kitchen Remodeling'}</SelectItem>
      <SelectItem value="mold_remediation">{language === 'es' ? 'Remediación de Moho' : 'Mold Remediation'}</SelectItem>
      <SelectItem value="new_construction">{language === 'es' ? 'Nueva Construcción' : 'New Construction'}</SelectItem>
      <SelectItem value="pool_construction">{language === 'es' ? 'Construcción de Piscina' : 'Pool Construction'}</SelectItem>
      <SelectItem value="popcorn_ceiling_removal">{language === 'es' ? 'Remoción de Techo Popcorn' : 'Popcorn Ceiling Removal'}</SelectItem>
      <SelectItem value="pressure_washing">{language === 'es' ? 'Lavado a Presión' : 'Pressure Washing'}</SelectItem>
      <SelectItem value="roofing">{language === 'es' ? 'Techos' : 'Roofing'}</SelectItem>
      <SelectItem value="room_painting">{language === 'es' ? 'Pintura de Habitación' : 'Room Painting'}</SelectItem>
      <SelectItem value="soffit_fascia">{language === 'es' ? 'Sofito y Fascia' : 'Soffit & Fascia'}</SelectItem>
      <SelectItem value="stucco_repair">{language === 'es' ? 'Reparación de Estuco' : 'Stucco Repair'}</SelectItem>
      <SelectItem value="tile_installation">{language === 'es' ? 'Instalación de Azulejos' : 'Tile Installation'}</SelectItem>
      <SelectItem value="water_heater_replacement">{language === 'es' ? 'Reemplazo de Calentador' : 'Water Heater Replacement'}</SelectItem>
      <SelectItem value="water_mitigation">{language === 'es' ? 'Mitigación de Agua' : 'Water Mitigation'}</SelectItem>
      <SelectItem value="window_replacement">{language === 'es' ? 'Reemplazo de Ventanas' : 'Window Replacement'}</SelectItem>
      {/* Commercial Projects */}
      <SelectItem value="commercial_driveways">{language === 'es' ? '🏢 Comercial - Entradas' : '🏢 Commercial - Driveways'}</SelectItem>
      <SelectItem value="commercial_drywall">{language === 'es' ? '🏢 Comercial - Drywall' : '🏢 Commercial - Drywall'}</SelectItem>
      <SelectItem value="commercial_electrical">{language === 'es' ? '🏢 Comercial - Eléctrico' : '🏢 Commercial - Electrical'}</SelectItem>
      <SelectItem value="commercial_exterior">{language === 'es' ? '🏢 Comercial - Exterior' : '🏢 Commercial - Exterior'}</SelectItem>
      <SelectItem value="commercial_fence">{language === 'es' ? '🏢 Comercial - Cercas' : '🏢 Commercial - Fence'}</SelectItem>
      <SelectItem value="commercial_hvac">{language === 'es' ? '🏢 Comercial - HVAC' : '🏢 Commercial - HVAC'}</SelectItem>
      <SelectItem value="commercial_painting">{language === 'es' ? '🏢 Comercial - Pintura' : '🏢 Commercial - Painting'}</SelectItem>
      <SelectItem value="commercial_plumbing">{language === 'es' ? '🏢 Comercial - Plomería' : '🏢 Commercial - Plumbing'}</SelectItem>
      <SelectItem value="commercial_pool">{language === 'es' ? '🏢 Comercial - Piscina' : '🏢 Commercial - Pool'}</SelectItem>
      <SelectItem value="commercial_roadways">{language === 'es' ? '🏢 Comercial - Carreteras' : '🏢 Commercial - Roadways'}</SelectItem>
      <SelectItem value="commercial_roofing">{language === 'es' ? '🏢 Comercial - Techos' : '🏢 Commercial - Roofing'}</SelectItem>
    </SelectContent>
  </Select>
);

const ContactInformationCard = ({ language, lead, handleInputChange }) => {
  const leadStatusOptions = getLeadStatusOptions(language);
  
  return (
    <Card>
      <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
        <CardTitle className="flex items-center">
          <User className="mr-2 h-5 w-5" />
          {language === 'es' ? 'Información de Contacto' : 'Contact Information'}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        <div>
          <Label>{language === 'es' ? 'Nombre del Cliente' : 'Client Name'} *</Label>
          <Input 
            value={lead.client_name || ''} 
            onChange={(e) => handleInputChange('client_name', e.target.value)} 
            data-testid="client-name" 
          />
        </div>
        <div>
          <Label>{language === 'es' ? 'Dirección' : 'Street Address'}</Label>
          <Input 
            value={lead.client_address || ''} 
            onChange={(e) => handleInputChange('client_address', e.target.value)} 
            placeholder={language === 'es' ? '123 Calle Principal' : '123 Main Street'}
            data-testid="client-address" 
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>{language === 'es' ? 'Ciudad' : 'City'}</Label>
            <Input 
              value={lead.client_city || ''} 
              onChange={(e) => handleInputChange('client_city', e.target.value)} 
              placeholder={language === 'es' ? 'Miami' : 'Miami'}
              data-testid="client-city" 
            />
          </div>
          <div>
            <Label>{language === 'es' ? 'Estado' : 'State'}</Label>
            <Input 
              value={lead.client_state || 'FL'} 
              onChange={(e) => handleInputChange('client_state', e.target.value)} 
              placeholder="FL"
              data-testid="client-state" 
            />
          </div>
          <div>
            <Label>{language === 'es' ? 'Código Postal' : 'ZIP Code'}</Label>
            <Input 
              value={lead.client_zip || ''} 
              onChange={(e) => handleInputChange('client_zip', e.target.value)} 
              placeholder="33101"
              data-testid="client-zip" 
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
            <Input 
              value={lead.client_phone || ''} 
              onChange={(e) => handleInputChange('client_phone', e.target.value)} 
              data-testid="client-phone" 
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input 
              type="email" 
              value={lead.client_email || ''} 
              onChange={(e) => handleInputChange('client_email', e.target.value)} 
              data-testid="client-email" 
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Website</Label>
            <Input 
              type="url" 
              value={lead.client_website || ''} 
              onChange={(e) => handleInputChange('client_website', e.target.value)} 
              placeholder="https://www.example.com" 
              data-testid="client-website" 
            />
          </div>
          <div>
            <Label>Google Business Profile</Label>
            <Input 
              type="url" 
              value={lead.client_google_business_profile || ''} 
              onChange={(e) => handleInputChange('client_google_business_profile', e.target.value)} 
              placeholder="https://g.page/business-name" 
              data-testid="client-google-business-profile" 
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{language === 'es' ? 'Estado del Lead' : 'Lead Status'}</Label>
            <Select value={lead.lead_status || 'estimate_provided'} onValueChange={(value) => handleInputChange('lead_status', value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {leadStatusOptions.map((status) => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{language === 'es' ? 'Categoría del Proyecto' : 'Project Category'}</Label>
            <ProjectCategorySelect 
              language={language} 
              value={lead.project_category} 
              onValueChange={(value) => handleInputChange('project_category', value)} 
            />
          </div>
        </div>
        
        {/* Lead Source Section */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t mt-4">
          <div>
            <Label>{language === 'es' ? 'Fuente del Lead' : 'Lead Source'}</Label>
            <Select value={lead.lead_source || ''} onValueChange={(value) => handleInputChange('lead_source', value)}>
              <SelectTrigger><SelectValue placeholder={language === 'es' ? 'Seleccionar fuente...' : 'Select source...'} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="warm_referral">{language === 'es' ? 'Referido Personal' : 'Warm Referral'}</SelectItem>
                <SelectItem value="cold_email">{language === 'es' ? 'Email Frío' : 'Cold Email'}</SelectItem>
                <SelectItem value="facebook">{language === 'es' ? 'Marketing Facebook' : 'Facebook Marketing'}</SelectItem>
                <SelectItem value="google">{language === 'es' ? 'Marketing Google' : 'Google Marketing'}</SelectItem>
                <SelectItem value="networking">{language === 'es' ? 'Grupo de Networking' : 'Networking Group'}</SelectItem>
                <SelectItem value="website">{language === 'es' ? 'Sitio Web' : 'Website'}</SelectItem>
                <SelectItem value="other">{language === 'es' ? 'Otro' : 'Other'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{language === 'es' ? 'Detalles de la Fuente' : 'Source Details'}</Label>
            <Input 
              value={lead.lead_source_description || ''} 
              onChange={(e) => handleInputChange('lead_source_description', e.target.value)}
              placeholder={language === 'es' ? "Ej: vecino de Susan, Harry" : "E.g., Susan's neighbor Harry"}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactInformationCard;
export { ProjectCategorySelect, getLeadStatusOptions };
